import React from 'react'
import { FIELD_CLASS, LABEL_CLASS } from './fieldStyles'

type Props = {
    id: string
    label: string
    value: string
    onChange: (value: string) => void
    maxLength: number
    /** Renders a textarea of this many rows; a single-line input when omitted. */
    rows?: number
    required?: boolean
    /** Shown once the value is over `maxLength`. */
    overLimitMessage: string
    /** Further validation messages for this field, e.g. a name-availability conflict. */
    children?: React.ReactNode
}

/**
 * A text field with a live character count.
 *
 * The control deliberately has no maxLength attribute: text pasted in over the limit
 * has to stay visible and editable, with the counter turning red, rather than being
 * silently truncated on the way in the way the database would truncate it.
 */
export default function CharCountField({
    id,
    label,
    value,
    onChange,
    maxLength,
    rows,
    required,
    overLimitMessage,
    children
}: Props) {
    const overLimit = value.length > maxLength

    return (
        <div>
            <div className="flex items-baseline justify-between">
                <label htmlFor={id} className={LABEL_CLASS}>
                    {label}
                </label>
                <span
                    className={`text-[11px] tabular-nums ${
                        overLimit ? 'font-semibold text-severe' : 'text-ink-400'
                    }`}
                >
                    {value.length}/{maxLength}
                </span>
            </div>
            {rows ? (
                <textarea
                    id={id}
                    rows={rows}
                    required={required}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className={FIELD_CLASS}
                />
            ) : (
                <input
                    id={id}
                    required={required}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className={FIELD_CLASS}
                />
            )}
            {overLimit && <p className="mt-1 text-xs text-severe">{overLimitMessage}</p>}
            {children}
        </div>
    )
}
